'use client';

/**
 * CanadaOfferingPage — `/ca/offering`, the Canadian campaign page reached from
 * "View full offering →" on `/ca/landing-page`.
 *
 * Ported from the "StartEngine Private Access Fund — Offering" handoff onto the
 * V3 editorial theme, so it shares tokens and typography with the landing page.
 * Unlike that page this one is a light (cream) surface throughout.
 *
 * Layout: opt-out bar → sticky header (wordmark + section links + Book a Call)
 * → hero (copy + image/status card) → section tab rail → two-column body
 * (article + sticky aside).
 *
 * Every "Book a Call" opens the existing sales-agent calendar, matching the
 * landing page.
 */
import { useEffect, useRef } from 'react';
import Link from 'next/link';
import * as Sentry from 'utils/errorTracking';
import GlobalStyles from '@mui/material/GlobalStyles';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { trackEvent, trackEventWithGTM } from 'utils/analytics';
import useCalendarBooking from 'components/Private/hooks/useCalendarBooking';
import { getSalesAgentCalendar } from 'services/user';
import { FALLBACK_CALENDAR_URL, OLD_GENERIC_ROUND_ROBIN } from 'utils/calendarConstants';
import EditorialRoot, {
  editorialScrollGlobalStyles,
} from 'components/HomepageV3/theme/globalStyles';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';
import { withSe3AssetPrefix } from 'components/HomepageV3/assetPrefix';
import CanadaOptOutBar from 'components/CanadaLandingPage/components/CanadaOptOutBar';
import { BELCO_EMAIL, BELCO_MAILTO } from 'components/CanadaLandingPage/data/canadaContent';
import { CANADA_LANDING_ROUTE } from 'components/CanadaLandingPage/data/canadaSite';
import {
  OFFERING_ABOUT_PARAGRAPHS,
  OFFERING_ASIDE_STATS,
  OFFERING_CONTACT,
  OFFERING_ELIGIBILITY_INTRO,
  OFFERING_HERO,
  OFFERING_OPPORTUNITY_PARAGRAPHS,
  OFFERING_REASONS,
  OFFERING_RISKS,
  OFFERING_SECTION_IDS,
  OFFERING_STEPS,
  OFFERING_TABS,
  OFFERING_TERMS,
  OFFERING_TERMS_FOOTNOTE,
} from './data/offeringContent';

const TRACKING_PAGE = 'canada_offering_page';

const WORDMARK_SRC = withSe3AssetPrefix('/homepage-v3/se-wordmark.png');
const HERO_IMAGE_SRC = withSe3AssetPrefix(OFFERING_HERO.imageSrc);

/* The handoff's campaign page is a light surface; its accents are the warmer
   gold/teal pair rather than the landing page's champagne-on-night. */
const ACCENT = '#B58A4B';
const TEAL_INK = '#185C65';
const BODY_INK = '#4C463E';
const MUTED_INK = '#6D665A';

const Page = styled('div')({
  background: COLORS.cream,
  color: COLORS.ink,
  fontFamily: FONTS.sans,
  minHeight: '100vh',
});

/* ─────────────── Header ─────────────── */

const Header = styled('header')({
  position: 'sticky',
  top: 0,
  zIndex: 50,
  background: 'rgba(252,249,245,0.92)',
  backdropFilter: 'saturate(180%) blur(10px)',
  borderBottom: `1px solid ${COLORS.edge}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '16px var(--pad)',
});

const HeaderLeft = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '40px',
  minWidth: 0,
});

const Brand = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

const BrandImg = styled('img')({
  height: 30,
  width: 'auto',
  display: 'block',
  // The shared wordmark asset is white; this page's header is cream.
  filter: 'invert(1) brightness(0.28) sepia(0.35) saturate(2.2) hue-rotate(140deg)',
});

const HeaderNav = styled('nav')({
  display: 'flex',
  gap: '28px',
  '@media (max-width: 900px)': { display: 'none' },
});

const HeaderNavLink = styled('a')({
  fontFamily: FONTS.sans,
  fontSize: '11.5px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: BODY_INK,
  textDecoration: 'none',
  '&:hover': { color: COLORS.ink },
});

const HeaderRight = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  flexShrink: 0,
});

const HomeLink = styled(Link)({
  fontFamily: FONTS.sans,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: BODY_INK,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { color: COLORS.ink },
  '@media (max-width: 640px)': { display: 'none' },
});

const HeaderCta = styled('button')({
  fontFamily: FONTS.sans,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: COLORS.cream,
  background: TEAL_INK,
  border: 'none',
  padding: '11px 18px',
  borderRadius: '12px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s',
  '&:hover': { background: '#12484F' },
});

/* ─────────────── Hero ─────────────── */

const HeroBand = styled('section')({
  background: COLORS.cream,
});

const HeroGrid = styled('div')({
  maxWidth: 1200,
  margin: '0 auto',
  padding: '48px var(--pad) 56px',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 480px',
  gap: '64px',
  alignItems: 'start',
  '@media (max-width: 1080px)': {
    gridTemplateColumns: '1fr',
    gap: '36px',
    padding: '36px var(--pad) 40px',
  },
});

const Chips = styled('div')({
  display: 'flex',
  gap: '10px',
  marginBottom: '24px',
  flexWrap: 'wrap',
});

const StatusChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  fontFamily: FONTS.sans,
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: TEAL_INK,
  border: '1px solid rgba(12,111,100,0.35)',
  background: 'rgba(1,209,184,0.07)',
  borderRadius: '999px',
  padding: '5px 11px',
});

const Dot = styled('span')({
  width: '5px',
  height: '5px',
  borderRadius: '50%',
  background: COLORS.teal,
});

const SeriesChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  fontFamily: FONTS.sans,
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: '#8A6A2E',
  border: '1px solid rgba(181,138,75,0.45)',
  background: 'rgba(181,138,75,0.07)',
  borderRadius: '999px',
  padding: '5px 11px',
});

const HeroEyebrow = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  fontWeight: 600,
  letterSpacing: '0.36em',
  textTransform: 'uppercase',
  color: ACCENT,
  marginBottom: '20px',
});

const HeroTitle = styled('h1')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: '46px',
  lineHeight: 1.1,
  letterSpacing: '-0.012em',
  color: COLORS.ink,
  textWrap: 'pretty',
  margin: '0 0 24px',
  '& em': { fontStyle: 'italic', color: TEAL_INK },
  '@media (max-width: 768px)': { fontSize: '32px' },
});

const HeroLead = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: '15px',
  lineHeight: 1.7,
  color: BODY_INK,
  maxWidth: '520px',
  margin: '0 0 28px',
});

const HeroCta = styled('button')({
  fontFamily: FONTS.sans,
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: COLORS.cream,
  background: TEAL_INK,
  border: 'none',
  padding: '16px 36px',
  borderRadius: '14px',
  cursor: 'pointer',
  boxShadow: '0 14px 30px rgba(11,41,45,0.16)',
  transition: 'background 0.15s, transform 0.15s',
  '&:hover': { background: '#12484F', transform: 'translateY(-1px)' },
});

const HeroNote = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: '11px',
  lineHeight: 1.6,
  color: MUTED_INK,
  maxWidth: '460px',
  margin: '22px 0 0',
});

const HeroImage = styled('img')({
  display: 'block',
  width: '100%',
  height: 'auto',
  borderRadius: '18px',
  border: `1px solid ${COLORS.edge}`,
});

const StatusCard = styled('div')({
  marginTop: '14px',
  background: COLORS.card,
  border: `1px solid ${COLORS.edge}`,
  borderTop: `2px solid ${ACCENT}`,
  borderRadius: '14px',
  boxShadow: '0 10px 26px rgba(11,41,45,0.06)',
  padding: '18px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
});

const StatusLabel = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: ACCENT,
});

const StatusValue = styled('div')({
  fontFamily: FONTS.serif,
  fontSize: '22px',
  fontWeight: 400,
  color: TEAL_INK,
  lineHeight: 1,
});

/* ─────────────── Tab rail ─────────────── */

const TabRail = styled('div')({
  borderBottom: `1px solid ${COLORS.edge}`,
  background: COLORS.cream,
});

const TabRailInner = styled('div')({
  maxWidth: 1200,
  margin: '0 auto',
  padding: '0 var(--pad)',
  display: 'flex',
  gap: 0,
  overflowX: 'auto',
});

const Tab = styled('a')({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: BODY_INK,
  textDecoration: 'none',
  borderBottom: '2px solid transparent',
  padding: '16px 18px 13px',
  whiteSpace: 'nowrap',
  '&:hover': { color: COLORS.ink, borderBottomColor: ACCENT },
});

/* ─────────────── Body ─────────────── */

const Body = styled('div')({
  maxWidth: 1200,
  margin: '0 auto',
  padding: '48px var(--pad) 64px',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 360px',
  gap: '56px',
  alignItems: 'start',
  '@media (max-width: 1080px)': {
    gridTemplateColumns: '1fr',
    gap: '36px',
  },
});

const Main = styled('main')({ minWidth: 0 });

const SectionLabel = styled('h2', {
  shouldForwardProp: (prop) => prop !== 'muted',
})<{ muted?: boolean }>(({ muted }) => ({
  fontFamily: FONTS.sans,
  fontWeight: 600,
  fontSize: '11px',
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: muted ? MUTED_INK : ACCENT,
  margin: '0 0 20px',
  scrollMarginTop: '150px',
}));

const ReasonList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  marginBottom: '40px',
});

const Reason = styled('div')({
  display: 'flex',
  gap: '16px',
  padding: '20px 22px',
  border: `1px solid ${COLORS.edge}`,
  borderLeft: `3px solid ${ACCENT}`,
  borderRadius: '12px',
  background: COLORS.card,
});

const ReasonNum = styled('div')({
  fontFamily: FONTS.serif,
  fontSize: '22px',
  color: ACCENT,
  lineHeight: 1.1,
  flexShrink: 0,
});

const ReasonTitle = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '15px',
  fontWeight: 700,
  color: COLORS.ink,
  marginBottom: '6px',
});

const ReasonDesc = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '14px',
  lineHeight: 1.65,
  color: BODY_INK,
});

const Prose = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: '15px',
  lineHeight: 1.72,
  color: BODY_INK,
  textWrap: 'pretty',
  margin: '0 0 24px',
  '&:last-of-type': { marginBottom: '40px' },
});

const EligibilityCard = styled('div')({
  background: COLORS.cream2,
  border: `1px solid ${COLORS.edge}`,
  borderRadius: '14px',
  padding: '24px 26px',
  marginBottom: '40px',
  '& p': {
    fontFamily: FONTS.sans,
    fontSize: '15px',
    lineHeight: 1.72,
    color: BODY_INK,
    textWrap: 'pretty',
    margin: '0 0 14px',
  },
  '& p:last-child': { marginBottom: 0 },
  '& a': { color: TEAL_INK, fontWeight: 600, textDecoration: 'none' },
  '& a:hover': { color: ACCENT },
});

const TermsHeading = styled('h3')({
  fontFamily: FONTS.serif,
  fontWeight: 500,
  fontSize: '21px',
  color: COLORS.ink,
  margin: '0 0 14px',
});

const TermsList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '24px',
});

const TermRow = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '14px',
  color: BODY_INK,
  lineHeight: 1.55,
  padding: '13px 0',
  borderBottom: `1px solid ${COLORS.edge}`,
  '& strong': { color: COLORS.ink, fontWeight: 600 },
});

const FootNote = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  lineHeight: 1.6,
  color: MUTED_INK,
  margin: '0 0 18px',
});

const DocLinks = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '18px',
  marginBottom: '44px',
});

const docLinkStyles = {
  fontFamily: FONTS.sans,
  fontSize: '13px',
  fontWeight: 600,
  color: TEAL_INK,
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  '&:hover': { color: ACCENT },
};

const DocButton = styled('button')(docLinkStyles);
const DocLink = styled('a')(docLinkStyles);

const StepList = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  marginBottom: '44px',
});

const Step = styled('div')({
  display: 'flex',
  gap: '16px',
  padding: '18px 20px',
  border: `1px solid ${COLORS.edge}`,
  borderRadius: '12px',
  background: COLORS.card,
  alignItems: 'flex-start',
});

const StepNum = styled('div')({
  fontFamily: FONTS.serif,
  fontSize: '18px',
  color: ACCENT,
  flexShrink: 0,
});

const StepBody = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '14px',
  lineHeight: 1.65,
  color: BODY_INK,
  '& strong': { color: COLORS.ink },
});

const Risks = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  fontFamily: FONTS.sans,
  fontSize: '11px',
  lineHeight: 1.65,
  color: MUTED_INK,
  '& p': { margin: 0 },
});

/* ─────────────── Aside ─────────────── */

const Aside = styled('aside')({
  position: 'sticky',
  top: '88px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  '@media (max-width: 1080px)': { position: 'static' },
});

const AsideCard = styled('div')({
  background: COLORS.card,
  border: `1px solid ${COLORS.edge}`,
  borderTop: `2px solid ${ACCENT}`,
  borderRadius: '18px',
  padding: '22px',
  boxShadow: '0 14px 34px rgba(11,41,45,0.06)',
});

const AsideCta = styled('button')({
  width: '100%',
  fontFamily: FONTS.sans,
  fontSize: '14px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: COLORS.cream,
  background: TEAL_INK,
  border: 'none',
  padding: '15px',
  borderRadius: '13px',
  cursor: 'pointer',
  lineHeight: 1.35,
  transition: 'background 0.15s',
  '&:hover': { background: '#12484F' },
  '& span': {
    display: 'block',
    fontSize: '10px',
    fontWeight: 500,
    letterSpacing: '0.08em',
    opacity: 0.7,
  },
});

const AsideStats = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '22px 18px',
  marginTop: '24px',
});

const AsideStatLabel = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: ACCENT,
  marginBottom: '6px',
});

const AsideStatValue = styled('div')({
  fontFamily: FONTS.serif,
  fontSize: '21px',
  fontWeight: 500,
  color: TEAL_INK,
  lineHeight: 1.2,
});

const ContactCard = styled('div')({
  background: COLORS.cream2,
  border: `1px solid ${COLORS.edge}`,
  borderRadius: '16px',
  padding: '18px 20px',
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
});

const ContactIcon = styled('div')({
  width: '42px',
  height: '42px',
  borderRadius: '50%',
  background: COLORS.card,
  border: `1px solid ${COLORS.edge}`,
  display: 'grid',
  placeItems: 'center',
  color: TEAL_INK,
  flexShrink: 0,
});

const ContactHeading = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '12px',
  fontWeight: 600,
  color: COLORS.ink,
  marginBottom: '2px',
});

const ContactName = styled('div')({
  fontFamily: FONTS.sans,
  fontSize: '12.5px',
  fontWeight: 600,
  color: TEAL_INK,
  marginBottom: '2px',
});

const ContactEmail = styled('a')({
  fontFamily: FONTS.sans,
  fontSize: '10px',
  color: MUTED_INK,
  textDecoration: 'none',
  '&:hover': { color: ACCENT },
});

export default function CanadaOfferingPage() {
  const hasTrackedView = useRef(false);

  const { data: calendarData } = useQuery({
    queryKey: ['salesAgentCalendar'],
    queryFn: () => getSalesAgentCalendar(),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
  const isCalendarApiEmpty =
    !calendarData?.calendarUrl ||
    calendarData.calendarUrl === OLD_GENERIC_ROUND_ROBIN ||
    calendarData.calendarUrl === null;
  const calendarUrl = isCalendarApiEmpty ? FALLBACK_CALENDAR_URL : calendarData.calendarUrl;
  const { openCalendar } = useCalendarBooking(calendarUrl);

  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    trackEvent('viewed_canada_offering_page', {
      page: TRACKING_PAGE,
      variant: 'se3',
    }).catch((e) => Sentry.captureException(e, { tags: { area: TRACKING_PAGE } }));
  }, []);

  const bookCall = (surface: string) => {
    try {
      trackEventWithGTM('canada_offering_book_a_call_clicked', {
        page: TRACKING_PAGE,
        surface,
        cta: OFFERING_HERO.ctaLabel,
      });
    } catch (e) {
      Sentry.captureException(e, { tags: { area: TRACKING_PAGE } });
    }
    openCalendar();
  };

  return (
    <>
      <GlobalStyles styles={editorialScrollGlobalStyles} />
      <EditorialRoot data-cy="canada-offering-page">
        <CanadaOptOutBar trackingPage={TRACKING_PAGE} />
        <Page>
          <Header data-cy="canada-offering-header">
            <HeaderLeft>
              <Brand
                href={CANADA_LANDING_ROUTE}
                aria-label="StartEngine"
                data-cy="canada-offering-brand"
              >
                <BrandImg src={WORDMARK_SRC} alt="StartEngine" />
              </Brand>
              <HeaderNav aria-label="Offering sections">
                {OFFERING_TABS.map((tab) => (
                  <HeaderNavLink key={tab.label} href={tab.href}>
                    {tab.label}
                  </HeaderNavLink>
                ))}
              </HeaderNav>
            </HeaderLeft>
            <HeaderRight>
              <HomeLink href={CANADA_LANDING_ROUTE} data-cy="canada-offering-home-link">
                ← Home
              </HomeLink>
              <HeaderCta
                type="button"
                onClick={() => bookCall('header')}
                data-cy="canada-offering-header-cta"
              >
                {OFFERING_HERO.ctaLabel}
              </HeaderCta>
            </HeaderRight>
          </Header>

          <HeroBand id={OFFERING_SECTION_IDS.overview} data-cy="canada-offering-hero">
            <HeroGrid>
              <div>
                <Chips>
                  <StatusChip>
                    <Dot />
                    {OFFERING_HERO.badge}
                  </StatusChip>
                  <SeriesChip>{OFFERING_HERO.seriesChip}</SeriesChip>
                </Chips>
                <HeroEyebrow>{OFFERING_HERO.eyebrow}</HeroEyebrow>
                <HeroTitle data-cy="canada-offering-title">
                  {OFFERING_HERO.title}
                  <em>{OFFERING_HERO.titleEm}</em>
                </HeroTitle>
                <HeroLead>{OFFERING_HERO.lead}</HeroLead>
                <div>
                  <HeroCta
                    type="button"
                    onClick={() => bookCall('hero')}
                    data-cy="canada-offering-hero-cta"
                  >
                    {OFFERING_HERO.ctaLabel}
                  </HeroCta>
                </div>
                <HeroNote>{OFFERING_HERO.ctaNote}</HeroNote>
              </div>
              <div>
                <HeroImage src={HERO_IMAGE_SRC} alt={OFFERING_HERO.imageAlt} />
                <StatusCard data-cy="canada-offering-status">
                  <StatusLabel>{OFFERING_HERO.statusLabel}</StatusLabel>
                  <StatusValue>{OFFERING_HERO.statusValue}</StatusValue>
                </StatusCard>
              </div>
            </HeroGrid>
          </HeroBand>

          <TabRail>
            <TabRailInner data-cy="canada-offering-tabs">
              {OFFERING_TABS.map((tab) => (
                <Tab key={tab.label} href={tab.href}>
                  {tab.label}
                </Tab>
              ))}
            </TabRailInner>
          </TabRail>

          <Body>
            <Main>
              <SectionLabel>Reasons to Consider</SectionLabel>
              <ReasonList data-cy="canada-offering-reasons">
                {OFFERING_REASONS.map((reason, index) => (
                  <Reason key={reason.title}>
                    <ReasonNum>{`0${index + 1}`}</ReasonNum>
                    <div>
                      <ReasonTitle>{reason.title}</ReasonTitle>
                      <ReasonDesc>{reason.desc}</ReasonDesc>
                    </div>
                  </Reason>
                ))}
              </ReasonList>

              <SectionLabel id={OFFERING_SECTION_IDS.about}>About the Offering</SectionLabel>
              {OFFERING_ABOUT_PARAGRAPHS.map((text) => (
                <Prose key={text.slice(0, 40)}>{text}</Prose>
              ))}

              <SectionLabel>The Opportunity</SectionLabel>
              {OFFERING_OPPORTUNITY_PARAGRAPHS.map((text) => (
                <Prose key={text.slice(0, 40)}>{text}</Prose>
              ))}

              <SectionLabel>Eligibility &amp; Suitability</SectionLabel>
              <EligibilityCard>
                <p>{OFFERING_ELIGIBILITY_INTRO}</p>
                <p>
                  For offering documents and subscription information, and to discuss whether a
                  StartEngine investment available in Canada is suitable for you, please contact
                  Warren Aarons, a registered dealing representative with Belco, at{' '}
                  <a href={BELCO_MAILTO}>{BELCO_EMAIL}</a>.
                </p>
              </EligibilityCard>

              <SectionLabel id={OFFERING_SECTION_IDS.terms}>Terms</SectionLabel>
              <TermsHeading>Offering overview</TermsHeading>
              <TermsList data-cy="canada-offering-terms">
                {OFFERING_TERMS.map((term) => (
                  <TermRow key={term.label}>
                    <strong>{term.label}</strong>: {term.value}
                  </TermRow>
                ))}
              </TermsList>
              <FootNote>{OFFERING_TERMS_FOOTNOTE}</FootNote>
              <DocLinks>
                <DocButton type="button" onClick={() => bookCall('offering_memorandum')}>
                  Offering Memorandum (on request)
                </DocButton>
                <DocButton type="button" onClick={() => bookCall('subscription_agreement')}>
                  Subscription Agreement (on request)
                </DocButton>
                <DocLink href={BELCO_MAILTO}>Contact Belco</DocLink>
              </DocLinks>

              <SectionLabel>How Investing Works</SectionLabel>
              <StepList data-cy="canada-offering-steps">
                {OFFERING_STEPS.map((step, index) => (
                  <Step key={step.lead}>
                    <StepNum>{index + 1}</StepNum>
                    <StepBody>
                      <strong>{step.lead}</strong> {step.desc}
                    </StepBody>
                  </Step>
                ))}
              </StepList>

              <SectionLabel muted>Risks &amp; Disclaimers</SectionLabel>
              <Risks data-cy="canada-offering-risks">
                {OFFERING_RISKS.map((text) => (
                  <p key={text.slice(0, 40)}>{text}</p>
                ))}
              </Risks>
            </Main>

            <Aside data-cy="canada-offering-aside">
              <AsideCard>
                <AsideCta
                  type="button"
                  onClick={() => bookCall('aside')}
                  data-cy="canada-offering-aside-cta"
                >
                  {OFFERING_HERO.ctaLabel}
                  <span>With a Belco dealing representative</span>
                </AsideCta>
                <AsideStats>
                  {OFFERING_ASIDE_STATS.map((stat) => (
                    <div key={stat.label}>
                      <AsideStatLabel>{stat.label}</AsideStatLabel>
                      <AsideStatValue>{stat.value}</AsideStatValue>
                    </div>
                  ))}
                </AsideStats>
              </AsideCard>
              <ContactCard>
                <ContactIcon>
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    aria-hidden="true"
                  >
                    <path d="M4 12a8 8 0 0 1 16 0v5a2 2 0 0 1-2 2h-1v-6h3M4 12v5a2 2 0 0 0 2 2h1v-6H4" />
                  </svg>
                </ContactIcon>
                <div>
                  <ContactHeading>{OFFERING_CONTACT.heading}</ContactHeading>
                  <ContactName>{OFFERING_CONTACT.name}</ContactName>
                  <ContactEmail href={BELCO_MAILTO}>{BELCO_EMAIL}</ContactEmail>
                </div>
              </ContactCard>
            </Aside>
          </Body>
        </Page>
      </EditorialRoot>
    </>
  );
}
